import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

/**
 * Durable persistence backstop for native (iOS/Android).
 *
 * Why this exists
 * ---------------
 * All non-secret app state — auth context (`paas_tenant_id`,
 * `paas_regional_api_url`, `paas_user`), the SSO community
 * list (`paas_sso_tenants`), the active community domain
 * (`paas_active_portal_domain`), branding (`paas_branding` / `paas_base_branding`)
 * and cached module/experience config — is written to **WKWebView
 * localStorage**. The refresh + SSO session tokens live in the Keychain (via
 * `secureStorage`, or behind the biometric), and the access token in memory only.
 *
 * On iOS the app is served from a custom scheme origin (`ip4cms://localhost`).
 * WKWebView localStorage under that origin is NOT reliably persisted across an
 * app kill / OS data eviction / reinstall, while the Keychain is. The observable
 * failure: after reopening the SSO app the user is still authenticated (Keychain
 * survived) but the Communities nav is empty and the community branding has
 * reverted to the hub — because `linkedTenants`, `active_portal_domain`, etc.
 * were silently dropped, so bootstrap re-resolves the configured hub domain.
 *
 * Fix
 * ---
 * Mirror every `paas_*` localStorage write (except credentials, see UNMIRRORED_KEYS) into
 * Capacitor Preferences (UserDefaults / SharedPreferences — durable across kill AND reinstall), and at
 * boot seed localStorage back from Preferences before the stores hydrate. Web is
 * untouched (localStorage there is already durable per-origin).
 */

const PREFIX = 'paas_';
const INSTALL_MARKER_KEY = 'install_marker';
let mirrorInstalled = false;

/**
 * Credentials and the member's profile snapshot. Preferences are plaintext and part of device
 * backups, so these never reach them. (No build writes `paas_access_token` any more; older builds'
 * copies are dropped on seed.)
 */
const UNMIRRORED_KEYS = new Set(['paas_access_token', 'paas_refresh_token', 'paas_user']);

const isMirrored = (key: string): boolean => key.startsWith(PREFIX) && !UNMIRRORED_KEYS.has(key);

const isNative = (): boolean => Capacitor.isNativePlatform();

/**
 * True once, on the first launch after an install. Preferences are wiped on uninstall while the
 * iOS Keychain is not, so empty Preferences mean any Keychain item belongs to a previous install.
 * No-op on web.
 */
export async function isFirstLaunchAfterInstall(): Promise<boolean> {
  if (!isNative()) return false;

  // 1. Already marked: an ordinary launch.
  const { value } = await Preferences.get({ key: INSTALL_MARKER_KEY });
  if (value) return false;

  // 2. No marker but mirrored keys: an update from a build that predates the marker. Its
  //    Preferences survived, so it was not reinstalled and its Keychain session is its own.
  const { keys } = await Preferences.keys();
  await Preferences.set({ key: INSTALL_MARKER_KEY, value: '1' });

  return keys.length === 0;
}

/**
 * Restore any `paas_*` keys that WKWebView localStorage dropped from the durable
 * Preferences mirror. Must run before the Zustand stores read localStorage
 * (authStore.hydrate, brandingStore._hydrate). Never clobbers a value already
 * present in localStorage (an in-session write is always fresher). No-op on web.
 */
export async function seedLocalStorageFromPrefs(): Promise<void> {
  if (!isNative()) return;

  const { keys } = await Preferences.keys();

  // Builds before UNMIRRORED_KEYS mirrored credentials too; drop the plaintext copies they left.
  await Promise.all(keys.filter((key) => UNMIRRORED_KEYS.has(key)).map((key) => Preferences.remove({ key })));

  await Promise.all(
    keys.filter(isMirrored).map(async (key) => {
      if (localStorage.getItem(key) != null) return;
      const { value } = await Preferences.get({ key });
      if (value != null) localStorage.setItem(key, value);
    }),
  );
}

/**
 * Write-through mirror: patch localStorage so every `paas_*` set/remove is also
 * applied to Capacitor Preferences. Idempotent; no-op on web. Install once at
 * boot, before any store writes, so the whole session is mirrored without having
 * to touch each store.
 */
export function installDurableMirror(): void {
  if (mirrorInstalled || !isNative()) return;

  const ls = window.localStorage;
  const origSet = ls.setItem.bind(ls);
  const origRemove = ls.removeItem.bind(ls);

  ls.setItem = (key: string, value: string): void => {
    origSet(key, value);
    if (isMirrored(key)) void Preferences.set({ key, value });
  };
  ls.removeItem = (key: string): void => {
    origRemove(key);
    if (isMirrored(key)) void Preferences.remove({ key });
  };
  mirrorInstalled = true;
}
